'use client'

import { useState, useEffect, Fragment, useRef, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { handleHome } from "@/(front-desk)/store"
import { Swiper, SwiperSlide } from "swiper/react"
import { Pagination } from 'swiper/modules'
import { ArrowRightIcon } from "@heroicons/react/20/solid"
import { reportError } from "@/(front-desk)/utils/functions"
import { LoadingIcon } from "@/(front-desk)/utils/patterns"
import ImageLoader from "@/(front-desk)/components/ImageLoader"
import classNames from "classnames"
import Player from "@vimeo/player"
import axios from "axios"
import background from "@/(front-desk)/assets/images/background/default.png"
import 'swiper/css'
import 'swiper/css/pagination'

const VimeoPlayer = ({ videoId, onReady, onPlay, onPause, onEnded, onError, loopVideo, index, playerState }) => {
    const containerRef = useRef(null)

    useEffect(() => {
        if (!containerRef.current || !videoId) return

        const player = new Player(containerRef.current, {
            id: videoId,
            controls: false,
            keyboard: false,
            loop: loopVideo,
            muted: true,
            autoplay: index === 0,
            responsive: true,
            autopause: false,
            title: false,
            byline: false,
            portrait: false,
            preload: 'metadata'
        })

        player.ready()
        .then(() => {
            onReady?.(player)
        })
        .catch((error) => {
            console.error('Vimeo player ready error:', error)
            onError?.(error)
        })

        player.on('play', () => {
            onPlay?.(player)
        })

        player.on('pause', () => {
            onPause?.(player)
        })

        player.on('ended', () => {
            onEnded?.(player)
        })

        player.on('error', (error) => {
            console.error('Vimeo player error:', error)
            onError?.(error)
        })

        return () => {
            player.destroy()
        }
    }, [videoId])

    return (
        <div
            ref={containerRef}
            className={classNames('video-player', {
                'opacity-0': playerState === 'ended'
            })}
        />
    )
}

const SLIDE_DURATION = 5000 // 5 seconds

const Slider = ({ size = 'lg' }) => {

    const dispatch = useDispatch()
    const { banners } = useSelector((state) => state.home)
    const [ swiperReady, setSwiperReady ] = useState(false)
    const [ playerStates, setPlayerStates ] = useState({}) // key by banner id
    const swiperRef = useRef(null)
    const playerRefs = useRef({}) // key by banner id
    const nextTimeout = useRef(null)
    const loopVideo = banners?.length === 1 && banners[0]?.carousel_type === 'video'

    const pauseAllVideos = useCallback(async () => {
        const promises = Object.values(playerRefs.current).map(async (player) => {
            if (player) {
                try {
                    await player.pause()
                }
                catch (err) {}
            }
        })
        await Promise.all(promises)
    }, [])

    const slideNext = useCallback((swiper) => {
        if (!swiper) return

        if (swiper.isEnd) {
            swiper.slideTo(0)
        }
        else {
            swiper.slideNext()
        }
    }, [])

    const handleSlideChange = useCallback(async (e) => {
        try {
            const bannerInfo = banners[e.realIndex]
            const bannerId = bannerInfo?.id
            const player = bannerId ? playerRefs.current[bannerId] : null

            // console.log(e.realIndex, bannerInfo?.carousel_type, player)

            if (nextTimeout.current) {
                clearTimeout(nextTimeout.current)
            }
            if (!swiperRef.current) {
                swiperRef.current = e
            }

            await pauseAllVideos()

            if (bannerInfo.carousel_type === 'image') {
                nextTimeout.current = setTimeout(() => {
                    slideNext(swiperRef.current)
                }, SLIDE_DURATION)
            }
            if (bannerInfo.carousel_type === 'video') {
                if (!player) return
                try {
                    player.setCurrentTime(0)
                    player.play()
                }
                catch (err) {
                    reportError(`Failed to play video: ${err.toString()}`, false)
                }
            }
        }
        catch (err) {
            reportError(`Error in slide change handler: ${err.toString()}`, false)
        }
    }, [banners, pauseAllVideos, slideNext, loopVideo])

    useEffect(() => {
        setSwiperReady(true)

        return () => {
            if (nextTimeout.current) {
                clearTimeout(nextTimeout.current)
            }
            playerRefs.current = {}
        }
    }, [])

    return (
        <div className="swiper-area">
            { !swiperReady || banners === null ? (
                <div className="full flex-center">
                    <LoadingIcon />
                </div>
            ) : (
                <Swiper
                    pagination={{clickable: true}}
                    speed={1000}
                    modules={[Pagination]}
                    slidesPerView={1}
                    onSwiper={handleSlideChange}
                    onRealIndexChange={handleSlideChange}
                >
                    { (banners === false || banners.length === 0) ? (
                        <SwiperSlide>
                            <ImageLoader src={background.src} loadingClassName="!bg-black" />
                        </SwiperSlide>
                    ) : (
                        banners.map((banner, index) => (
                            // console.log('banner', banner),
                            <SwiperSlide key={`banner-${banner.id}`}>
                                { banner.carousel_type === 'image' &&
                                    <ImageLoader
                                        src={(size === 'lg' ? banner.image_lg : banner.image_sm) || background.src}
                                        loadingClassName="!bg-black"
                                        alt="放視大賞網站主視覺 Banner"
                                    />
                                }
                                { banner.carousel_type === 'video' &&
                                    <Fragment>
                                        <VimeoPlayer
                                            videoId={size === 'lg' ? banner.video_lg_id : banner.video_sm_id}
                                            loopVideo={loopVideo}
                                            index={index}
                                            playerState={playerStates[banner.id]}
                                            onReady={(player) => {
                                                // console.log('👍', 'ready', player)
                                                playerRefs.current[banner.id] = player
                                            }}
                                            onPlay={(player) => {
                                                // console.log('▶️', 'play', player)
                                                setPlayerStates(prev => ({ ...prev, [banner.id]: 'playing' }))

                                                if (loopVideo) return
                                                nextTimeout.current = setTimeout(() => {
                                                    slideNext(swiperRef.current)
                                                }, SLIDE_DURATION)
                                            }}
                                            onPause={(player) => {
                                                // console.log('⏸️', 'pause', player)
                                                setPlayerStates(prev => ({ ...prev, [banner.id]: 'paused' }))
                                            }}
                                            onEnded={(player) => {
                                                // console.log('🎬', 'video end', player)
                                                setPlayerStates(prev => ({ ...prev, [banner.id]: 'ended' }))
                                                if (loopVideo) return
                                                slideNext(swiperRef.current)
                                            }}
                                            onError={(error) => {
                                                reportError(`Vimeo Player Error: ${size === 'lg' ? banner.video_lg_id : banner.video_sm_id}`, false)

                                                // Remove the banner with error
                                                const updatedBanners = banners.filter(b => b.id !== banner.id)
                                                if (updatedBanners.length === 0) {
                                                    dispatch(handleHome.actions.setBanners(false))
                                                }
                                                else {
                                                    dispatch(handleHome.actions.setBanners(updatedBanners))
                                                }
                                            }}
                                        />
                                    </Fragment>
                                }
                                <div className="detail">
                                    { banner.subtitle &&
                                        <div className="sub-title mb-1">{banner.subtitle}</div>
                                    }
                                    { banner.title &&
                                        <div className="title mt-1">{banner.title}</div>
                                    }
                                    { banner.description &&
                                        <div className="text mt-2">{banner.description}</div>
                                    }
                                    { banner.target_link &&
                                        <a
                                            href={banner.target_link}
                                            className="btn btn-round btn-main mt-10 w-fit"
                                            target={banner.new_tab ? '_blank' : '_self'}
                                            rel="noreferrer"
                                        >
                                            立即前往 <ArrowRightIcon className="w-5" />
                                        </a>
                                    }
                                </div>
                            </SwiperSlide>
                        ))
                    )}
                </Swiper>
            )}
        </div>
    )
}

export default function Banner() {

    const dispatch = useDispatch()
    const { banners } = useSelector((state) => state.home)
    const [ size, setSize ] = useState('lg')
    const resizeTimeout = useRef(null)

    const getBannerList = useCallback(() => {
        axios.get(`/api/v1/cms/zh_hant/home`) // temperary hardcode to zh_hant
        .then((res) => {
            // console.log(res.config?.url, res.data.body.carousel_items)
            if (res.data.body?.carousel_items?.length > 0) {
                dispatch(handleHome.actions.setBanners(res.data.body.carousel_items))
            }
            else {
                dispatch(handleHome.actions.setBanners(false))
            }
        })
        .catch((err) => {
            // console.log(err.config?.url, err)
            dispatch(handleHome.actions.setBanners(false))
            reportError(err, false)
        })
    }, [dispatch])

    useEffect(() => {
        const updateSize = () => {
            if (window.innerWidth < 1024) {
                setSize('sm')
            }
            else {
                setSize('lg')
            }
        }
        updateSize()
        const handleResize = () => {
            if (resizeTimeout.current) {
                clearTimeout(resizeTimeout.current)
            }
            resizeTimeout.current = setTimeout(() => {
                updateSize()
            }, 150)
        }

        window.addEventListener('resize', handleResize)

        if (!banners) {
            getBannerList()
        }

        return () => {
            window.removeEventListener('resize', handleResize)
            if (resizeTimeout.current) {
                clearTimeout(resizeTimeout.current)
            }
        }
    }, [banners, getBannerList])

    // Generate key based on size and banner IDs to force remount when banners change
    const sliderKey = `${size}-${banners ? banners.map(b => b.id).join(',') : 'loading'}`

    return <Slider key={sliderKey} size={size} />
}
